import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Save, ChevronDown, ChevronUp, Stethoscope, Users, FileText,
  GraduationCap, Brain, Activity, AlertTriangle, ListChecks, ClipboardList
} from 'lucide-react';

// ─── Evaluation Area Definitions ───
const EVAL_AREAS = [
  { key: 'cognitive', label: 'Cognitive / Intelligence', icon: Brain },
  { key: 'academic', label: 'Academic Achievement', icon: GraduationCap },
  { key: 'speech_language', label: 'Speech / Language', icon: FileText },
  { key: 'occupational_therapy', label: 'Occupational Therapy', icon: Activity },
  { key: 'physical_therapy', label: 'Physical Therapy', icon: Activity },
  { key: 'behavioral_emotional', label: 'Behavioral / Emotional', icon: AlertTriangle },
  { key: 'adaptive_behavior', label: 'Adaptive Behavior', icon: ListChecks },
  { key: 'social_skills', label: 'Social Skills', icon: Users },
  { key: 'assistive_technology', label: 'Assistive Technology', icon: ClipboardList },
  { key: 'other', label: 'Other', icon: FileText },
];

const PARENT_FIELDS = [
  { key: 'medical_history', label: 'Medical History', icon: Stethoscope },
  { key: 'social_history', label: 'Social History', icon: Users },
  { key: 'developmental_history', label: 'Developmental History', icon: FileText },
];

const EDU_HISTORY_FIELDS = [
  { key: 'previous_schools', label: 'Previous Schools', icon: GraduationCap },
  { key: 'attendance', label: 'Attendance', icon: ClipboardList },
];

function buildDefaults() {
  const evalAreas = {};
  EVAL_AREAS.forEach(a => { evalAreas[a.key] = { has_data: false, summary: '' }; });
  return {
    parent_information: { medical_history: '', social_history: '', developmental_history: '' },
    educational_history: { previous_schools: '', attendance: '' },
    previous_evaluations: evalAreas,
  };
}

function mergeContent(existing) {
  const defaults = buildDefaults();
  if (!existing || typeof existing !== 'object') return defaults;
  const merged = JSON.parse(JSON.stringify(defaults));
  if (existing.parent_information) Object.assign(merged.parent_information, existing.parent_information);
  if (existing.educational_history) Object.assign(merged.educational_history, existing.educational_history);
  if (existing.previous_evaluations && typeof existing.previous_evaluations === 'object') {
    EVAL_AREAS.forEach(a => {
      const pe = existing.previous_evaluations[a.key];
      if (pe && typeof pe === 'object') merged.previous_evaluations[a.key] = { ...merged.previous_evaluations[a.key], ...pe };
      else if (pe && typeof pe === 'string') merged.previous_evaluations[a.key] = { has_data: true, summary: pe };
    });
  }
  return merged;
}

function CollapsibleGroup({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button className="flex items-center justify-between w-full mb-3 px-1 group" onClick={() => setOpen(!open)}>
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959]">{title}</h3>
        {open ? <ChevronUp className="h-3.5 w-3.5 text-[#595959]" /> : <ChevronDown className="h-3.5 w-3.5 text-[#595959]" />}
      </button>
      {open && children}
    </div>
  );
}

export default function ReviewExistingDataEditor({ section, caseData, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(section?.content));
  useEffect(() => { setContent(mergeContent(section?.content)); }, [section?.section_id]);

  const handleSave = () => onSave(content);

  const update = (path, value) => {
    setContent(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      let ref = next;
      for (let i = 0; i < path.length - 1; i++) {
        if (!ref[path[i]] || typeof ref[path[i]] !== 'object') ref[path[i]] = {};
        ref = ref[path[i]];
      }
      ref[path[path.length - 1]] = value;
      return next;
    });
  };

  const evalAreasWithData = EVAL_AREAS.filter(a => content.previous_evaluations?.[a.key]?.has_data).length;

  return (
    <div className="max-w-4xl space-y-8" data-testid="review-data-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="review-data-title">Review of Existing Data</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl shrink-0" data-testid="review-data-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save All'}
        </Button>
      </div>

      {/* Parent / Guardian Information */}
      <CollapsibleGroup title="Parent / Guardian Information">
        <div className="space-y-3">
          {PARENT_FIELDS.map(f => {
            const Icon = f.icon;
            return (
              <div key={f.key} className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid={`red-parent-${f.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="h-4 w-4 text-[#5A8A5A]" />
                  <label className="text-sm font-medium text-[#1C1C1C]">{f.label}</label>
                  {(content.parent_information?.[f.key] || '').trim() && <span className="text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-full ml-auto">Has content</span>}
                </div>
                <Textarea value={content.parent_information?.[f.key] || ''} onChange={e => update(['parent_information', f.key], e.target.value)}
                  placeholder={`Enter ${f.label.toLowerCase()} information...`} rows={3} className="rounded-xl border-[#E5E5E5] text-sm"
                  data-testid={`red-parent-input-${f.key}`} />
              </div>
            );
          })}
        </div>
      </CollapsibleGroup>

      {/* Educational History */}
      <CollapsibleGroup title="Educational History">
        <div className="space-y-3">
          {EDU_HISTORY_FIELDS.map(f => {
            const Icon = f.icon;
            return (
              <div key={f.key} className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid={`red-edu-${f.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="h-4 w-4 text-[#5A8A5A]" />
                  <label className="text-sm font-medium text-[#1C1C1C]">{f.label}</label>
                </div>
                <Textarea value={content.educational_history?.[f.key] || ''} onChange={e => update(['educational_history', f.key], e.target.value)}
                  placeholder={`Enter ${f.label.toLowerCase()} information...`} rows={3} className="rounded-xl border-[#E5E5E5] text-sm"
                  data-testid={`red-edu-input-${f.key}`} />
              </div>
            );
          })}
        </div>
      </CollapsibleGroup>

      {/* Previous Evaluations & Assessments */}
      <CollapsibleGroup title={`Previous Evaluations & Assessments (${evalAreasWithData}/${EVAL_AREAS.length} areas have data)`}>
        <div className="space-y-3">
          {EVAL_AREAS.map(area => {
            const Icon = area.icon;
            const data = content.previous_evaluations?.[area.key] || { has_data: false, summary: '' };
            return (
              <div key={area.key} className={`border rounded-2xl overflow-hidden transition-colors ${data.has_data ? 'bg-white border-[#5A8A5A]/30' : 'bg-[#FAFAF8] border-[#E5E5E5]'}`} data-testid={`red-eval-area-${area.key}`}>
                <div className="flex items-center justify-between px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <Icon className={`h-4 w-4 ${data.has_data ? 'text-[#5A8A5A]' : 'text-[#999]'} shrink-0`} />
                    <span className="text-sm font-medium text-[#1C1C1C]">{area.label}</span>
                    {data.has_data && data.summary?.trim() && <span className="text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Has data</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[#595959]">Prior data exists?</span>
                    <button className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${data.has_data ? 'bg-[#5A8A5A] text-white' : 'bg-[#E5E5E5] text-[#595959] hover:bg-[#d9d9d9]'}`}
                      onClick={() => update(['previous_evaluations', area.key, 'has_data'], !data.has_data)}
                      data-testid={`red-eval-toggle-${area.key}`}>
                      {data.has_data ? 'Yes' : 'No'}
                    </button>
                  </div>
                </div>
                {data.has_data && (
                  <div className="px-5 pb-5 border-t border-[#E5E5E5]/50">
                    <Textarea value={data.summary || ''} onChange={e => update(['previous_evaluations', area.key, 'summary'], e.target.value)}
                      placeholder={`Summarize prior ${area.label.toLowerCase()} evaluation data, dates, instruments used, and key findings...`}
                      rows={3} className="rounded-xl border-[#E5E5E5] text-sm mt-3"
                      data-testid={`red-eval-input-${area.key}`} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CollapsibleGroup>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl" data-testid="review-data-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Review of Existing Data'}
        </Button>
      </div>
    </div>
  );
}
